import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from './SearchableDropdown';
import { getSocietyRoles } from '../redux/actions/SocietyPortal/RolePermissionAction/SocietyRoleAction';
import { selectedSocietyStorer } from '../utils/webStorer';
import usePermissions from '../hook/usePermissions';
import { PERMISSIONS } from '../consts/permissions';

// Dropdown of society roles for assigning a login role to an Owner/Tenant/
// Family member/Vendor. Hidden unless the active user has user.manage_roles —
// the backend defaults to RESIDENT (occupant) or VENDOR when role is omitted.
const RoleSelect = ({
    value,
    onChange,
    societyId,
    placeholder = 'Select role',
    disabled = false,
    className = '',
    requirePermission = true,
}) => {
    const dispatch = useDispatch();
    const { rolesData, loading } = useSelector((s) => s.SocietyRoleReducer);
    const { can } = usePermissions();

    const effectiveSocietyId = societyId || selectedSocietyStorer.getSocietyId();

    useEffect(() => {
        if (effectiveSocietyId && !rolesData && !loading) {
            dispatch(getSocietyRoles({ society_id: effectiveSocietyId }));
        }
    }, [dispatch, effectiveSocietyId, rolesData, loading]);

    const options = useMemo(() => {
        const rows = Array.isArray(rolesData) ? rolesData : rolesData?.rows || [];
        return rows.map((r) => ({
            role_id: r.role_id,
            label: r.is_system_role ? r.role_name : `${r.role_name} (custom)`,
        }));
    }, [rolesData]);

    if (requirePermission && !can(PERMISSIONS.USER_MANAGE_ROLES)) return null;

    return (
        <SearchableDropdown
            value={value || ''}
            onChange={onChange}
            options={options}
            valueKey="role_id"
            labelKey="label"
            placeholder={loading ? 'Loading…' : placeholder}
            loading={loading}
            disabled={disabled || loading}
            className={className}
        />
    );
};

// Resolve the default role_id for a given role_code from the cached roles list.
// Returns undefined when roles haven't loaded yet or the code isn't present —
// callers should omit role_id from the payload in that case so the backend default kicks in.
export const useDefaultRoleId = (roleCode) => {
    const rolesData = useSelector((s) => s.SocietyRoleReducer?.rolesData);
    return useMemo(() => {
        const rows = Array.isArray(rolesData) ? rolesData : rolesData?.rows || [];
        return rows.find((r) => r.role_code === roleCode)?.role_id;
    }, [rolesData, roleCode]);
};

export default RoleSelect;
